import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Leaf, Heart, Sparkles, Calendar, Users, Gift, Flag, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = '#10B981';
const ACCENT_LIGHT = '#34D399';
const BENEFIT_ICONS = { 0: Leaf, 1: Sparkles, 2: Flag, 3: Gift, 4: Heart, 5: Sparkles, 6: Users, 7: Calendar, 8: Sparkles, 9: Heart };

/**
 * Vendor-only Founding Non-Profit tab.
 * Restricted to vendors with vendor_type === 'nonprofit'.
 */
export default function FoundingNonProfitTab() {
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);
  const [story, setStory] = useState('');
  const [savingStory, setSavingStory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [statusRes, meRes] = await Promise.all([
        fetch(`${API}/founding-nonprofits/status`),
        fetch(`${API}/vendors/me`, { headers: getAuthHeaders() }),
      ]);
      if (statusRes.ok) setStatus(await statusRes.json());
      if (meRes.ok) {
        const v = await meRes.json();
        setVendor(v);
        setStory(v?.founding_nonprofit_story || '');
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const fNP = params.get('founding_nonprofit');
    if (sessionId && fNP === 'success' && vendor) {
      (async () => {
        const res = await fetch(`${API}/founding-nonprofits/confirm/${sessionId}`, {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const d = await res.json();
          if (d.status === 'paid') {
            toast.success(`🌱 Welcome, Founding Non-Profit #${d.founding_number}!`);
            window.history.replaceState(null, '', window.location.pathname);
            load();
          }
        }
      })();
    }
  }, [vendor]);

  const startCheckout = async () => {
    setCheckingOut(true);
    try {
      const res = await fetch(`${API}/founding-nonprofits/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          success_url: `${window.location.origin}/dashboard/vendor/founding-nonprofit?founding_nonprofit=success`,
          cancel_url: `${window.location.origin}/dashboard/vendor/founding-nonprofit?founding_nonprofit=cancelled`,
        }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data?.detail || 'Could not start checkout');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCheckingOut(false);
    }
  };

  const saveStory = async () => {
    setSavingStory(true);
    try {
      const res = await fetch(`${API}/founding-nonprofits/me/story`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ story }),
      });
      if (res.ok) toast.success('Story saved');
      else toast.error('Failed to save');
    } finally {
      setSavingStory(false);
    }
  };

  if (loading) return <div className="text-[#9CA3AF] p-12 text-center">Loading…</div>;
  if (!status) return null;

  const isNonProfit = vendor?.vendor_type === 'nonprofit';
  const isFounder = vendor?.is_founding_nonprofit;
  const pct = Math.min(100, (status.slots_taken / status.slots_total) * 100);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="founding-nonprofit-tab">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl p-8 sm:p-12"
        style={{
          background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.10) 50%, rgba(12,14,23,1) 100%)',
          border: `1px solid ${ACCENT}66`,
          boxShadow: '0 30px 80px -20px rgba(16,185,129,0.35)',
        }}
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-[120px]" style={{ background: `${ACCENT}33` }} />

        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-5"
            style={{ background: `${ACCENT}33`, border: `1px solid ${ACCENT}66`, color: ACCENT_LIGHT }}>
            <Leaf size={12} /> LIMITED TO 10 NON-PROFITS · CROWDFUNDING A GLOBAL INDIGENOUS ECONOMY
          </div>

          <h1 className="font-syne text-3xl sm:text-5xl font-bold text-white mb-3">
            Become a <span style={{ color: ACCENT_LIGHT }}>Founding Non-Profit</span>
          </h1>
          <p className="text-emerald-100/85 text-base sm:text-lg mb-6 max-w-2xl">
            Help us build a global Indigenous marketplace. For a one-time <strong style={{ color: ACCENT_LIGHT }}>${status.price_usd.toLocaleString()}</strong>, your organization becomes one of only <strong style={{ color: ACCENT_LIGHT }}>10</strong> founding partners who will forever be celebrated as co-architects of this platform.
          </p>

          <div className="mb-6 max-w-md">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-emerald-100/60">
                Seats remaining: <span className="font-bold" style={{ color: ACCENT_LIGHT }}>{status.slots_remaining}</span> / {status.slots_total}
              </span>
              <span className="text-xs font-mono" style={{ color: ACCENT_LIGHT }} data-testid="nonprofit-tab-slot-count">
                {status.slots_taken}/{status.slots_total}
              </span>
            </div>
            <div className="h-3 rounded-full bg-white/5 border border-white/10 overflow-hidden">
              <motion.div
                className="h-full"
                style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_LIGHT})` }}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.8 }}
              />
            </div>
          </div>

          {isFounder ? (
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold"
              style={{ background: 'rgba(16,185,129,0.15)', border: `1px solid ${ACCENT}66`, color: ACCENT_LIGHT }}
              data-testid="nonprofit-founder-active">
              <Check size={16} /> You are Founding Non-Profit #{vendor.founding_nonprofit_number} — thank you.
            </div>
          ) : !isNonProfit ? (
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 font-semibold" data-testid="nonprofit-gate">
              <X size={16} /> Only non-profit organizations can claim these seats. Update your vendor type to "Non-Profit" in Edit Profile.
            </div>
          ) : status.is_sold_out ? (
            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-[#9CA3AF] font-semibold">
              <X size={16} /> All 10 seats filled. Thank you!
            </div>
          ) : (
            <button
              onClick={startCheckout}
              disabled={checkingOut}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-[#0E0E23] text-base font-bold shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)] disabled:opacity-50"
              style={{ background: `linear-gradient(135deg, ${ACCENT_LIGHT} 0%, ${ACCENT} 100%)` }}
              data-testid="nonprofit-checkout-btn"
            >
              {checkingOut ? <><Loader2 className="animate-spin" size={18} /> Redirecting…</> : <><Leaf size={18} /> Claim Founding Non-Profit Seat · ${status.price_usd.toLocaleString()}</>}
            </button>
          )}
        </div>
      </motion.div>

      {/* Benefits grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="nonprofit-benefits">
        {status.benefits.map((b, i) => {
          const Icon = BENEFIT_ICONS[i] || Sparkles;
          return (
            <div key={i} className="p-4 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-3">
              <div className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center"
                style={{ background: `${ACCENT}26`, border: `1px solid ${ACCENT}4d` }}>
                <Icon size={16} style={{ color: ACCENT_LIGHT }} />
              </div>
              <div>
                <div className="text-white font-semibold text-sm mb-1">{b.title}</div>
                <div className="text-[#9CA3AF] text-xs">{b.desc}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="p-5 rounded-2xl" style={{ background: 'rgba(16,185,129,0.06)', border: `1px solid ${ACCENT}33` }}>
        <p className="text-emerald-100/85 text-sm">
          <strong className="text-white">Est. value: $3,000+</strong> · Price: <strong style={{ color: ACCENT_LIGHT }}>${status.price_usd.toLocaleString()}</strong> — one-time, never a renewal.
        </p>
      </div>

      {isFounder && (
        <div className="p-5 rounded-2xl bg-white/5 border border-white/10" data-testid="nonprofit-story-editor">
          <h3 className="font-syne text-lg font-bold text-white mb-2">Your Founding Non-Profit Story</h3>
          <p className="text-[#9CA3AF] text-xs mb-3">
            This is what shows on our <a className="underline" style={{ color: ACCENT_LIGHT }} href="/founding-nonprofits" target="_blank" rel="noreferrer">appreciation page</a> — your mission, community, and why you joined us early. 2,000 characters max.
          </p>
          <textarea
            value={story}
            onChange={(e) => setStory(e.target.value.slice(0, 2000))}
            className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm min-h-[140px]"
            placeholder="Tell the story of your organization's mission, who you serve, and why you chose to be one of the first 10 non-profit partners…"
            data-testid="nonprofit-story-textarea"
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-[#64748B]">{story.length}/2000</span>
            <button
              onClick={saveStory}
              disabled={savingStory}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-[#0E0E23] disabled:opacity-50"
              style={{ background: `linear-gradient(135deg, ${ACCENT_LIGHT} 0%, ${ACCENT} 100%)` }}
              data-testid="save-nonprofit-story"
            >
              {savingStory ? 'Saving…' : 'Save Story'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
